import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

// Calendly Integration Component
const CalendlyButton = ({ 
  text, 
  variant = "primary", 
  size = "medium",
  className = "",
  showMicrocopy = true,
  icon = "📅"
}) => {
  const { t } = useTranslations();
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";
  
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 text-center";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg",
    secondary: "border-2 border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300",
    outline: "border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40",
    accent: "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg"
  };
  
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <div className="text-center">
      <a
        href={calendlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        aria-label={t('booking.calendar.title')}
      >
        <span className="mr-2">{icon}</span>
        {text || t('booking.bookSession')}
      </a>
      {showMicrocopy && (
        <p className="text-sm text-gray-400 mt-2">
          {t('booking.freeSessionMicrocopy')}
        </p>
      )}
    </div>
  );
};

const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('booking.navigation.home')}</Link>
            <Link to="/demo" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('booking.navigation.demo')}</Link>
            <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('booking.navigation.pricing')}</Link>
            <Link to="/asesoria" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('booking.navigation.consulting')}</Link>
            <Link to="/reserva" className="text-green-400 hover:text-green-300 transition-colors text-sm lg:text-base font-semibold">{t('booking.navigation.booking')}</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('booking.navigation.contact')}</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
              aria-label={t('booking.navigation.openMenu')}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('booking.navigation.home')}</Link>
              <Link to="/demo" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('booking.navigation.demo')}</Link>
              <Link to="/pricing" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('booking.navigation.pricing')}</Link>
              <Link to="/asesoria" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('booking.navigation.consulting')}</Link>
              <Link to="/reserva" className="block text-green-400 hover:text-green-300 py-2 transition-colors font-semibold" onClick={closeMobileMenu}>{t('booking.navigation.booking')}</Link>
              <Link to="/contacto" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('booking.navigation.contact')}</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const ReservaPage = () => {
  const { t } = useTranslations();
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update meta tags based on language
  useEffect(() => {
    // Update document title
    document.title = t('booking.meta.title');
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = t('booking.meta.description');
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = t('booking.meta.keywords');
    
    // Update Open Graph tags for social sharing
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = t('booking.meta.title');
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = t('booking.meta.description');
    
  }, [t]); // Re-run when translations change

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header Section */}
      <section className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            {t('booking.backToHome')}
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-6">
              {t('booking.pageTitle')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t('booking.pageSubtitle')}
            </p>
            
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              <div className="bg-slate-800/30 p-6 rounded-xl border border-green-500/20">
                <div className="text-3xl mb-3">✅</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('booking.benefits.totallyFree.title')}</h3>
                <p className="text-gray-300 text-sm">{t('booking.benefits.totallyFree.description')}</p>
              </div>
              <div className="bg-slate-800/30 p-6 rounded-xl border border-green-500/20">
                <div className="text-3xl mb-3">⚡</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('booking.benefits.effectiveTime.title')}</h3>
                <p className="text-gray-300 text-sm">{t('booking.benefits.effectiveTime.description')}</p>
              </div>
              <div className="bg-slate-800/30 p-6 rounded-xl border border-green-500/20">
                <div className="text-3xl mb-3">🎯</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('booking.benefits.personalized.title')}</h3>
                <p className="text-gray-300 text-sm">{t('booking.benefits.personalized.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendly Embedded Section */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
            
            {/* iframe Container */}
            <div className="relative">
              {!iframeLoaded && (
                <div className="flex items-center justify-center h-96 bg-slate-700/50 rounded-xl">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                    <p className="text-gray-300">{t('booking.calendar.loading')}</p>
                  </div>
                </div>
              )}
              
              <iframe 
                src={`https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?embed_domain=${window.location.hostname}&embed_type=Inline&utm_source=ybv&utm_medium=site&utm_campaign=free_session`}
                width="100%" 
                height="700" 
                frameBorder="0"
                title={t('booking.calendar.title')}
                className={`rounded-xl ${iframeLoaded ? 'block' : 'hidden'}`}
                onLoad={() => setIframeLoaded(true)}
                style={{ minHeight: '700px' }}
              />
            </div>

            {/* Alternative Options */}
            <div className="mt-8 text-center">
              <p className="text-gray-400 mb-4">
                {t('booking.calendar.problemsText')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <CalendlyButton 
                  text={t('booking.calendar.openNewWindow')}
                  variant="accent"
                  size="medium"
                  showMicrocopy={false}
                />
                <Link
                  to="/contacto"
                  className="border-2 border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                >
                  {t('booking.calendar.contactDirectly')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('booking.whatToExpect.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-800/40 p-6 rounded-xl border border-white/10 text-center">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('booking.whatToExpect.initialAnalysis.title')}</h3>
              <p className="text-gray-300 text-sm">{t('booking.whatToExpect.initialAnalysis.description')}</p>
            </div>
            <div className="bg-slate-800/40 p-6 rounded-xl border border-white/10 text-center">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('booking.whatToExpect.opportunityIdentification.title')}</h3>
              <p className="text-gray-300 text-sm">{t('booking.whatToExpect.opportunityIdentification.description')}</p>
            </div>
            <div className="bg-slate-800/40 p-6 rounded-xl border border-white/10 text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('booking.whatToExpect.actionPlan.title')}</h3>
              <p className="text-gray-300 text-sm">{t('booking.whatToExpect.actionPlan.description')}</p>
            </div>
            <div className="bg-slate-800/40 p-6 rounded-xl border border-white/10 text-center">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('booking.whatToExpect.nextSteps.title')}</h3>
              <p className="text-gray-300 text-sm">{t('booking.whatToExpect.nextSteps.description')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservaPage;